import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProviderCard, { startingPrice } from "@/components/ProviderCard";
import ProviderProfileModal from "@/components/ProviderProfileModal";
import { Disclaimer } from "@/components/Disclaimers";
import { Loader2, Building2, SlidersHorizontal, Info } from "lucide-react";

const money = (n) => `$${Number(n).toLocaleString()}`;

export default function Providers() {
  const [providers, setProviders] = useState(null);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const [city, setCity] = useState("all");
  const [specialty, setSpecialty] = useState("all");
  const [accreditation, setAccreditation] = useState("all");
  const [sortBy, setSortBy] = useState("rating");

  const load = () => {
    setError(false);
    setProviders(null);
    api.get("/v1/providers")
      .then((r) => setProviders(Array.isArray(r.data) ? r.data : []))
      .catch(() => { setError(true); setProviders([]); });
  };
  useEffect(load, []);

  const viewProfile = (p) => { setSelected(p); setOpen(true); };

  const cities = useMemo(
    () => Array.from(new Set((providers || []).map((p) => p.city))).sort(),
    [providers]);
  const specialties = useMemo(
    () => Array.from(new Set((providers || []).flatMap((p) => p.specialties || []))).sort(),
    [providers]);

  const filtered = useMemo(() => {
    let list = (providers || []).filter((p) => {
      if (city !== "all" && p.city !== city) return false;
      if (specialty !== "all" && !(p.specialties || []).includes(specialty)) return false;
      if (accreditation !== "all" && !(p.accreditation || []).includes(accreditation)) return false;
      return true;
    });
    const cmp = {
      rating: (a, b) => (b.rating || 0) - (a.rating || 0),
      "price-asc": (a, b) => (startingPrice(a) ?? Infinity) - (startingPrice(b) ?? Infinity),
      "price-desc": (a, b) => (startingPrice(b) ?? -Infinity) - (startingPrice(a) ?? -Infinity),
      name: (a, b) => a.name.localeCompare(b.name),
    }[sortBy];
    // Keep featured providers on top, then apply chosen sort.
    return [...list].sort((a, b) => (Number(b.featured) - Number(a.featured)) || cmp(a, b));
  }, [providers, city, specialty, accreditation, sortBy]);

  const resetFilters = () => { setCity("all"); setSpecialty("all"); setAccreditation("all"); setSortBy("rating"); };

  return (
    <div className="bg-[#F7F9FC] min-h-screen">
      <section className="mv-gradient-hero text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold flex items-center gap-3">
            <Building2 size={40} /> Accredited Providers
          </h1>
          <p className="mt-3 text-white/85 max-w-2xl text-lg">Explore India's leading NABH/JCI-accredited hospitals across its major cities — view full profiles, compare specialties, and book with confidence.</p>
        </div>
      </section>

      {/* Filter bar */}
      {providers && providers.length > 0 && (
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E4E7EC]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1.5 text-sm font-medium text-[#475467]"><SlidersHorizontal size={15} /> Filters</span>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="w-[160px] h-9 rounded-full" data-testid="filter-city"><SelectValue placeholder="City" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={specialty} onValueChange={setSpecialty}>
              <SelectTrigger className="w-[190px] h-9 rounded-full" data-testid="filter-specialty"><SelectValue placeholder="Specialty" /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">All Specialties</SelectItem>
                {specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={accreditation} onValueChange={setAccreditation}>
              <SelectTrigger className="w-[160px] h-9 rounded-full" data-testid="filter-accreditation"><SelectValue placeholder="Accreditation" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accreditations</SelectItem>
                <SelectItem value="JCI">JCI</SelectItem>
                <SelectItem value="NABH">NABH</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[170px] h-9 rounded-full" data-testid="filter-sort"><SelectValue placeholder="Sort by" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">Top Rated</SelectItem>
                <SelectItem value="price-asc">Price: Low to High</SelectItem>
                <SelectItem value="price-desc">Price: High to Low</SelectItem>
                <SelectItem value="name">Name (A–Z)</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={resetFilters} className="text-sm text-[#0D5C75] hover:underline ml-auto" data-testid="filter-reset">Reset</button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        {!providers ? (
          <div className="py-20 grid place-items-center text-muted-foreground"><Loader2 className="animate-spin" /></div>
        ) : error ? (
          <div className="py-16 text-center" data-testid="providers-error">
            <Building2 size={40} className="mx-auto text-[#98A2B3]" />
            <h3 className="font-heading font-bold text-lg text-[#101828] mt-4">Couldn't load providers</h3>
            <p className="text-muted-foreground text-sm mt-1">Please check your connection and try again.</p>
            <Button className="mt-5 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white" onClick={load} data-testid="providers-retry-btn">Retry</Button>
          </div>
        ) : providers.length === 0 ? (
          <div className="py-16 text-center" data-testid="providers-empty">
            <Building2 size={40} className="mx-auto text-[#98A2B3]" />
            <h3 className="font-heading font-bold text-lg text-[#101828] mt-4">No providers available yet</h3>
            <p className="text-muted-foreground text-sm mt-1">Please check back soon.</p>
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between mb-6">
              <p className="text-sm text-muted-foreground" data-testid="providers-count">
                Showing <span className="font-semibold text-[#101828]">{filtered.length}</span> provider{filtered.length === 1 ? "" : "s"}
              </p>
            </div>
            {filtered.some((p) => p.featured) && (
              <p className="-mt-3 mb-6 text-xs text-muted-foreground flex items-start gap-1.5" data-testid="featured-disclosure">
                <Info size={13} className="mt-0.5 shrink-0 text-[#98A2B3]" />
                <span>Providers marked <span className="font-semibold text-[#F97316]">Featured</span> are paid promotional placements (sponsored). Paid placement is not an endorsement of clinical quality by SarvaSeva.</span>
              </p>
            )}
            {filtered.length === 0 ? (
              <div className="py-16 text-center" data-testid="providers-no-match">
                <Building2 size={40} className="mx-auto text-[#98A2B3]" />
                <h3 className="font-heading font-bold text-lg text-[#101828] mt-4">No providers match your filters</h3>
                <Button className="mt-5 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white" onClick={resetFilters} data-testid="clear-filters-btn">Clear Filters</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 items-stretch">
                {filtered.map((p, i) => (
                  <ProviderCard key={p.id} p={p} i={i} onView={viewProfile} />
                ))}
              </div>
            )}
            <Disclaimer testid="providers-list-disclaimer" className="mt-10 max-w-3xl">
              Accreditation status (NABH/JCI) is verified at onboarding and revalidated periodically. Prices shown are starting estimates and are confirmed by the provider after medical evaluation.
            </Disclaimer>
          </>
        )}
      </div>

      <ProviderProfileModal provider={selected} open={open} onOpenChange={setOpen} />
    </div>
  );
}
